import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'

import { ApiCallError } from '../api/client'
import { t, tOr } from '../i18n'
import { landing, sameStop, tabStops } from './tabStops'

/** The four pieces of chrome every screen in this application reuses. */

export function Pending({ label }: { label?: string }) {
  return (
    <p className="muted" role="status">
      {label ?? t('app.loading')}
    </p>
  )
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'good' | 'warn' | 'bad'
  children: ReactNode
}) {
  return (
    <div className="notice" data-tone={tone} role={tone === 'bad' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

/**
 * A failure, shown as the API described it.
 *
 * The instance answers a refusal with a message key rather than a sentence,
 * because a stored sentence is a stored English sentence. When the client
 * knows the key it renders the string; when it does not it renders the key,
 * which is ugly and traceable and much better than "Something went wrong".
 *
 * A refusal that names something — a tool, a state, the day a window ran out —
 * sends those values beside the key, and they are rendered into the sentence
 * here. A brace the refusal sent no value for is left where it is rather than
 * blanked, because a sentence with a hole visible in it is a defect somebody
 * can see and report, and a sentence quietly missing a word is not.
 */
export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  const key = error instanceof ApiCallError ? error.messageKey : undefined
  const args = error instanceof ApiCallError ? error.messageArgs : undefined
  const status = error instanceof ApiCallError ? error.status : undefined
  const fields = error instanceof ApiCallError ? error.fields : undefined
  const detail = key
    ? tOr(key, key, args)
    : fields
      ? t('app.error.fields')
      : error instanceof Error
        ? error.message
        : String(error)
  return (
    <Notice tone="bad">
      <strong>{t('app.error_title')}</strong>
      <div className="muted">{detail}</div>
      {fields ? (
        <ul>
          {fields.map((problem) => (
            <li key={problem.field}>
              {t(problem.kind === 'missing' ? 'app.error.field_missing' : 'app.error.field_invalid', {
                field: fieldLabel(problem.field),
              })}
            </li>
          ))}
        </ul>
      ) : null}
      {retry ? (
        <p style={{ margin: '0.5rem 0 0' }}>
          <button type="button" onClick={retry}>
            {t('app.retry')}
          </button>
        </p>
      ) : null}
      {/* The code is for whoever is asked to look into it, not for the person
          reading the sentence above: kept, and folded away under its own
          heading rather than printed beside the sentence as if it explained it. */}
      {status ? (
        <details className="technical-details">
          <summary>{t('app.error.technical')}</summary>
          <span className="mono">{t('app.error.technical_status', { status })}</span>
        </details>
      ) : null}
    </Notice>
  )
}

/**
 * A field the request named, said as words.
 *
 * The request names it the way the API does (`manager_person_id`), and the
 * form's own label is not known here. A catalogue entry under `field.<name>`
 * wins when there is one; otherwise the name is read out with its underscores
 * and its trailing `id` dropped, which is plainer than the code and still the
 * same field.
 */
function fieldLabel(name: string): string {
  const words = name.replace(/_id$/, '').replace(/_/g, ' ')
  return tOr(`field.${name}`, words.charAt(0).toUpperCase() + words.slice(1))
}

const TONES: Record<string, 'open' | 'good' | 'warn' | 'bad'> = {
  draft: 'warn',
  issued: 'open',
  open: 'open',
  reopened: 'warn',
  disputed: 'bad',
  ready_for_verification: 'open',
  answered: 'good',
  closed_draft: 'warn',
  verified: 'good',
  closed: 'good',
  void: 'bad',
  critical: 'bad',
  major: 'warn',
  minor: 'open',
  // A submittal's state, and its disposition, which are two fields and not one.
  // "The review finished" and "the review said yes" are different facts — the
  // module's own enum says so and the equipment register needs the second one —
  // so `closed` is toned as a finished thing and the decision carries its own
  // chip beside it. A `closed` submittal that was rejected reads as closed and
  // green on its state and rejected and red on its decision, which is the
  // truth; colouring the state by the decision would hide that one of them is
  // still a question.
  in_review: 'open',
  // Correspondence. Sent and received are the same moment from two sides;
  // response due is the one status somebody has to act on.
  sent: 'open',
  received: 'open',
  response_due: 'warn',
  responded: 'good',
  returned: 'warn',
  approved: 'good',
  approved_as_noted: 'good',
  revise_and_resubmit: 'warn',
  rejected: 'bad',
  // Read and filed, approving nothing. Neither good nor bad, and not a warning:
  // "for record" is an ordinary outcome and a yellow chip would read as a
  // problem somebody has to chase.
  for_record: 'open',
}

/** A state or a severity, coloured by meaning rather than by module. */
export function Chip({ value, prefix }: { value: string; prefix: string }) {
  return (
    <span className="chip" data-tone={TONES[value] ?? undefined}>
      {tOr(`${prefix}.${value}`, value)}
    </span>
  )
}

/**
 * A modal surface that behaves: focus goes into it, Escape closes it, and the
 * page behind it does not scroll away under a tablet's thumb.
 */
export function Overlay({
  kind,
  titleId,
  onClose,
  children,
}: {
  kind: 'drawer' | 'dialog'
  titleId: string
  onClose: () => void
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previous = document.activeElement
    // The first control that can take focus, or the panel itself when there is
    // none, so focus is never left behind an open surface.
    if (panel.current) (tabStops(panel.current)[0] ?? panel.current).focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      // Focus lost from inside the surface -- the focused control removed or
      // disabled -- lands on the page, where the panel's own handler never
      // hears Tab. Taken back here, by the innermost open surface only.
      if (
        event.key === 'Tab' &&
        !event.defaultPrevented &&
        panel.current &&
        (document.activeElement === null || document.activeElement === document.body) &&
        !panel.current.querySelector('[aria-modal="true"]')
      ) {
        event.preventDefault()
        ;(tabStops(panel.current)[0] ?? panel.current).focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [onClose])

  // Tab and Shift+Tab go round the surface rather than out of it to the page
  // behind, which a modal says is not there (E1-64). Handled on the panel, and
  // only for keys pressed in this surface rather than in one opened inside it,
  // which wraps on its own.
  const onTab = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || event.defaultPrevented || !panel.current) return
    if ((event.target as Element).closest('[aria-modal="true"]') !== panel.current) return
    const stops = tabStops(panel.current)
    const first = stops[0]
    const last = stops[stops.length - 1]
    const at = document.activeElement
    if (!first || !last) {
      event.preventDefault()
      panel.current.focus()
    } else if (at === panel.current || !panel.current.contains(at)) {
      // On the panel itself (the fallback): into the surface from the end the
      // key points to.
      event.preventDefault()
      ;(event.shiftKey ? landing(last, true, panel.current) : first).focus()
    } else if (event.shiftKey && sameStop(at, first, panel.current)) {
      event.preventDefault()
      landing(last, true, panel.current).focus()
    } else if (!event.shiftKey && sameStop(at, last, panel.current)) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div
        className={kind}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
        tabIndex={-1}
        onKeyDown={onTab}
      >
        {children}
      </div>
    </>
  )
}

/**
 * Whether the form a button sits in is ready to send, saying what is not.
 *
 * The forms whose actions are `type="button"` (two actions from one form, or a
 * write that is not a submit) never reach the browser's own required-field
 * check, so a field marked `required` was sent empty and came back as a
 * refusal. This runs that check from the button: an empty required field is
 * pointed at in place, with everything typed so far kept, and nothing is sent.
 */
export function formReady(button: HTMLButtonElement): boolean {
  return button.form?.reportValidity() ?? true
}

/**
 * A plain calendar date, `2026-10-15`, with no time and no zone.
 *
 * The API sends a date-only field (a meeting day, a due date, a required-on-site
 * date) in this form, and `new Date("2026-10-15")` reads it as midnight UTC.
 * Shown in the browser's own zone that is the evening before anywhere west of
 * Greenwich, so every such field read a day early in New York. A calendar day
 * is the same day in every zone, so it is built from its parts in the local one.
 */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

/** A date the way a site writes one. Imperial units, US dates, no exceptions. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const day = CALENDAR_DAY.exec(value)
  const parsed = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
